/**
 * Types + helpers for the Platform Stats → Feature Flags admin page.
 * Mirrors the shapes returned by dream-be `routes/platform-stats-feature-flags.ts`.
 */

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

export type FlagKind = 'boolean' | 'string' | 'number' | 'json' | 'variant';

export type FlagRuleScope =
  | 'user'
  | 'company'
  | 'organisation'
  | 'ops_role'
  | 'email_domain';

export type FlagBucketKey = 'user' | 'company' | 'organisation' | 'thread';

export interface FlagVariant {
  key: string;
  weight: number;
}

export interface FlagDefinition {
  kind: FlagKind;
  default: JsonValue;
  description: string;
  owner: string;
  category: string;
  devinSession?: string;
  expiresAt?: string;
  variants?: FlagVariant[];
  bucketBy?: FlagBucketKey;
}

export interface FlagState {
  key: string;
  kind: string;
  description: string;
  owner: string;
  category: string;
  devinSession: string | null;
  expiresAt: string | null;
  defaultValue: JsonValue;
  killSwitch: boolean;
  rolloutPercent: number | null;
  rolloutBucketBy: string;
  rolloutSalt: string;
  rolloutValue: JsonValue;
  variants: JsonValue;
  archivedAt: string | null;
  archivedBy: string | null;
  createdAt: string;
  updatedAt: string;
  updatedBy: string | null;
  updatedReason: string | null;
}

export interface FlagRule {
  id: number;
  flagKey: string;
  scope: FlagRuleScope;
  scopeId: string;
  value: JsonValue;
  note: string | null;
  createdAt: string;
  createdBy: string | null;
}

export interface FlagSummary {
  key: string;
  definition: FlagDefinition | null;
  state: FlagState | null;
  rules: FlagRule[];
  inRegistry: boolean;
}

export interface FlagListResponse {
  flags: FlagSummary[];
  scopes: FlagRuleScope[];
  bucketKeys: FlagBucketKey[];
}

export interface FlagHistoryRow {
  id: number;
  flagKey: string;
  tableName: 'feature_flags' | 'feature_flag_rules';
  op: 'INSERT' | 'UPDATE' | 'DELETE';
  rowBefore: Record<string, JsonValue> | null;
  rowAfter: Record<string, JsonValue> | null;
  changedAt: string;
  changedBy: string;
  reason: string | null;
}

export const SCOPE_LABELS: Record<FlagRuleScope, string> = {
  user: 'User',
  company: 'Company',
  organisation: 'Organisation',
  ops_role: 'Ops role',
  email_domain: 'Email domain',
};

export const SCOPE_ID_HINTS: Record<FlagRuleScope, string> = {
  user: 'auth0|…',
  company: 'numeric company id',
  organisation: 'org_…',
  ops_role: 'e.g. admin',
  email_domain: 'e.g. usedream.ai',
};

export const BUCKET_LABELS: Record<FlagBucketKey, string> = {
  user: 'User',
  company: 'Company',
  organisation: 'Organisation',
  thread: 'Thread',
};

export function formatJson(v: JsonValue | undefined): string {
  if (v === undefined) return '—';
  if (typeof v === 'string') return JSON.stringify(v);
  return JSON.stringify(v);
}

/** Text shown in an editor for a stored value; strings are shown raw, everything else as JSON. */
export function valueToInput(kind: FlagKind, v: JsonValue | undefined): string {
  if (v === undefined || v === null) return '';
  if (kind === 'string' || kind === 'variant') return String(v);
  if (kind === 'json') return JSON.stringify(v, null, 2);
  return String(v);
}

/** Inverse of valueToInput; throws with a user-facing message on bad input. */
export function inputToValue(kind: FlagKind, raw: string): JsonValue {
  switch (kind) {
    case 'boolean':
      if (raw === 'true') return true;
      if (raw === 'false') return false;
      throw new Error('Boolean value must be true or false');
    case 'number': {
      const n = Number(raw.trim());
      if (raw.trim() === '' || !Number.isFinite(n)) {
        throw new Error('Number value must be a finite number');
      }
      return n;
    }
    case 'string':
    case 'variant':
      return raw;
    case 'json':
      try {
        return JSON.parse(raw) as JsonValue;
      } catch {
        throw new Error('JSON value is not valid JSON');
      }
  }
}

function isVariantList(
  v: JsonValue | undefined,
): v is FlagVariant[] & JsonValue {
  return (
    Array.isArray(v) &&
    v.every(
      (x) =>
        typeof x === 'object' &&
        x !== null &&
        !Array.isArray(x) &&
        typeof x.key === 'string' &&
        typeof x.weight === 'number',
    )
  );
}

export function flagVariants(
  definition: FlagDefinition | null,
  state: FlagState | null,
): FlagVariant[] {
  if (definition?.variants) return definition.variants;
  if (state && isVariantList(state.variants)) return state.variants;
  return [];
}

export type FlagStatus =
  | 'archived'
  | 'kill'
  | 'rollout'
  | 'rules'
  | 'on'
  | 'off'
  | 'set';

export function flagStatus(flag: FlagSummary): FlagStatus {
  const s = flag.state;
  if (!s || s.archivedAt) return 'archived';
  if (s.killSwitch) return 'kill';
  if (s.rolloutPercent != null) return 'rollout';
  if (flag.rules.length > 0) return 'rules';
  if (s.kind === 'boolean') return s.defaultValue === true ? 'on' : 'off';
  return 'set';
}

export const STATUS_LABELS: Record<FlagStatus, string> = {
  archived: 'Archived',
  kill: 'Kill switch',
  rollout: 'Rollout',
  rules: 'Rules',
  on: 'On',
  off: 'Off',
  set: 'Default',
};

/**
 * What changes for users if this flag is archived right now. Archiving skips the
 * DB default, kill switch, rollout and rules and returns the code default to
 * everyone, so each line names a group whose value would flip. Empty ⇒ no-op.
 */
export function archiveImpact(flag: FlagSummary): string[] {
  const s = flag.state;
  const d = flag.definition;
  if (!s || !d || s.archivedAt) return [];
  const codeDefault = JSON.stringify(d.default);
  const differs = (v: JsonValue | undefined) =>
    JSON.stringify(v ?? null) !== codeDefault;
  const to = `→ ${formatJson(d.default)}`;
  const out: string[] = [];

  if (s.killSwitch) {
    if (differs(s.defaultValue)) {
      out.push(
        `Kill switch is on, so everyone currently gets ${formatJson(
          s.defaultValue,
        )} ${to}.`,
      );
    }
    return out;
  }

  const affectedRules = flag.rules.filter((r) => differs(r.value));
  if (affectedRules.length > 0) {
    const first = affectedRules[0];
    const n = affectedRules.length;
    out.push(
      `${n} of ${flag.rules.length} rule${
        flag.rules.length === 1 ? '' : 's'
      } stop${n === 1 ? 's' : ''} applying, e.g. ${first.scope}:${
        first.scopeId
      } ${formatJson(first.value)} ${to}.`,
    );
  }

  if (s.rolloutPercent != null) {
    const rolloutValue =
      s.kind === 'boolean'
        ? (s.rolloutValue ?? true)
        : (s.rolloutValue ?? s.defaultValue);
    if (differs(rolloutValue)) {
      out.push(
        `The ${s.rolloutPercent}% rollout (by ${
          s.rolloutBucketBy
        }) stops: bucketed users ${formatJson(rolloutValue)} ${to}.`,
      );
    }
  }

  if (differs(s.defaultValue)) {
    out.push(`Everyone else: DB default ${formatJson(s.defaultValue)} ${to}.`);
  }
  return out;
}

const HISTORY_FIELDS_IGNORED = new Set([
  'updatedAt',
  'updatedBy',
  'updatedReason',
]);

/** Human-readable one-liner for a history row, e.g. `killSwitch: false → true`. */
export function describeHistoryRow(row: FlagHistoryRow): string {
  const before = row.rowBefore ?? {};
  const after = row.rowAfter ?? {};
  if (row.tableName === 'feature_flag_rules') {
    const r = row.op === 'DELETE' ? before : after;
    const scope = typeof r.scope === 'string' ? r.scope : '?';
    const scopeId = typeof r.scopeId === 'string' ? r.scopeId : '?';
    const verb =
      row.op === 'INSERT'
        ? 'Added'
        : row.op === 'DELETE'
          ? 'Removed'
          : 'Changed';
    return `${verb} rule ${scope}:${scopeId} = ${formatJson(r.value)}`;
  }
  if (row.op === 'INSERT')
    return `Flag created (default ${formatJson(after.defaultValue)})`;
  if (row.op === 'DELETE') return 'Flag row deleted';
  const changes: string[] = [];
  for (const field of Object.keys({ ...before, ...after })) {
    if (HISTORY_FIELDS_IGNORED.has(field)) continue;
    const a = JSON.stringify(before[field] ?? null);
    const b = JSON.stringify(after[field] ?? null);
    if (a !== b) changes.push(`${field}: ${a} → ${b}`);
  }
  return changes.length ? changes.join(', ') : 'No field changes';
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-GB');
}

/** Extracts the backend `error` message from an axios-style failure. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err !== null) {
    const e = err as {
      response?: { data?: { error?: unknown } };
      message?: unknown;
    };
    const apiErr = e.response?.data?.error;
    if (typeof apiErr === 'string' && apiErr) return apiErr;
    if (typeof e.message === 'string' && e.message) return e.message;
  }
  return fallback;
}
